<script setup lang="ts">
/**
 * One loading / error / empty vocabulary for a section that waits on data (errors-10, visual-3).
 *
 * The dashboard used to show a "Loading..." line, daisyUI spinners, a hand-rolled spinner and 100px PrimeVue spinners
 * at once, each replaced by content of a different height, with an empty state in some lists and none in others and
 * no way to retry. This renders, by `status`:
 *  - 'pending': a skeleton sized like the loaded content (or the `#skeleton` slot), hidden from screen readers, which
 *    hear `label` from a role="status" line instead;
 *  - 'error': a role="alert" box with plain copy and a real "Try again" button when the parent listens to `retry`;
 *  - 'success' with `isEmpty`: the empty state (or the `#empty` slot, plus `#empty-actions` inside the default one);
 *  - 'success': the default slot, dimmed while `refreshing`.
 * The root carries aria-busy while pending or refreshing. A Pinia store's flags, or a Colada query's, become `status`
 * through toAsyncStatus() (@/composables/to-async-status), which keeps loaded data on screen through a reload.
 */
import { computed } from "vue";

import type { AsyncStatus } from "@/composables/to-async-status";
import { failureDetail } from "@/composables/useNotify";

export type AsyncSkeleton = 'rows' | 'card' | 'chart';

const props = withDefaults( defineProps< {
  status: AsyncStatus;
  isEmpty?: boolean;
  /** A load is running behind content already shown: dims it, and disables "Try again" while a retry runs. */
  refreshing?: boolean;
  /** The failure. Offline, timeout and server failures get plain copy; anything else shows `errorHint`, never its message. */
  error?: unknown;
  skeleton?: AsyncSkeleton;
  /** How many rows (or cards) the skeleton draws; defaults to 3 rows, 2 cards, 1 chart. */
  rows?: number;
  /** The height of each skeleton row, card or chart, as a class (e.g. 'h-24'), so it matches the loaded content. */
  rowClass?: string;
  /** Screen-reader text while pending. */
  label?: string;
  errorTitle?: string;
  errorHint?: string;
  emptyTitle?: string;
  emptyHint?: string;
  /** A PrimeIcons class for the default empty state. */
  emptyIcon?: string;
  /** Prefix of the `-loading`, `-error` and `-empty` test ids. */
  testid?: string;
  /** Classes for the wrapper around the loaded content, e.g. a flex column that a chart with its own scroller fills. */
  contentClass?: string;
  /**
   * `@retry` on the parent. A prop rather than an emit so the button is only rendered when someone handles it: a
   * "Try again" that does nothing is worse than none.
   */
  onRetry?: () => unknown;
} >(), {
  isEmpty: false,
  refreshing: false,
  error: undefined,
  skeleton: 'rows',
  rows: undefined,
  rowClass: undefined,
  label: 'Loading…',
  errorTitle: "This section couldn't be loaded.",
  errorHint: 'Please try again in a moment.',
  emptyTitle: 'Nothing to show yet',
  emptyHint: '',
  emptyIcon: 'pi-inbox',
  testid: 'async-state',
  contentClass: undefined,
  onRetry: undefined
} );

const SKELETON_DEFAULTS: Readonly< Record< AsyncSkeleton, { count: number, rowClass: string } > > = {
  rows: { count: 3, rowClass: 'h-5' },
  card: { count: 2, rowClass: 'h-40' },
  chart: { count: 1, rowClass: 'h-80' }
};

const count = computed< number >( () => props.rows ?? SKELETON_DEFAULTS[ props.skeleton ].count );
const blockClass = computed< string >( () => props.rowClass ?? SKELETON_DEFAULTS[ props.skeleton ].rowClass );
const busy = computed< boolean >( () => props.status === 'pending' || props.refreshing );
// Server messages are written for developers and can leak internals, so only the classified plain copy replaces the hint
const errorDetail = computed< string >( () => props.error ? failureDetail( props.error, props.errorHint ) : props.errorHint );

function retry(): void {
  props.onRetry?.();
}
</script>

<template>
  <div :aria-busy="busy">
    <div v-if="status === 'pending'" :data-testid="`${ testid }-loading`">
      <span class="sr-only" role="status">{{ label }}</span>
      <div aria-hidden="true">
        <slot name="skeleton">
          <div v-if="skeleton === 'card'" class="grid gap-3" :class="{ 'md:grid-cols-2': count > 1 }">
            <div v-for="n in count" :key="n" class="skeleton w-full rounded-box" :class="blockClass"></div>
          </div>
          <div v-else-if="skeleton === 'chart'" class="skeleton w-full rounded-box" :class="blockClass"></div>
          <div v-else class="space-y-2">
            <div v-for="n in count" :key="n" class="skeleton w-full rounded-xl" :class="blockClass"></div>
          </div>
        </slot>
      </div>
    </div>

    <div v-else-if="status === 'error'" role="alert" class="alert alert-warning text-sm" :data-testid="`${ testid }-error`">
      <i class="pi pi-exclamation-triangle" aria-hidden="true"></i>
      <div>
        <p class="font-medium">{{ errorTitle }}</p>
        <p class="opacity-80">{{ errorDetail }}</p>
      </div>
      <button v-if="onRetry" type="button" class="btn btn-sm" :disabled="refreshing" @click="retry">
        <span v-if="refreshing" class="loading loading-spinner loading-xs" aria-hidden="true"></span>
        Try again
      </button>
    </div>

    <template v-else-if="isEmpty">
      <slot name="empty">
        <div class="rounded-xl border border-dashed border-base-300/80 bg-base-200/30 p-6 text-center text-base-content/60"
             :data-testid="`${ testid }-empty`">
          <i class="pi text-2xl text-base-content/40" :class="emptyIcon" aria-hidden="true"></i>
          <p class="mt-2 font-medium text-base-content/70">{{ emptyTitle }}</p>
          <p v-if="emptyHint" class="mt-1 text-sm">{{ emptyHint }}</p>
          <slot name="empty-actions"></slot>
        </div>
      </slot>
    </template>

    <div v-else class="transition-opacity" :class="[ contentClass, { 'opacity-60': refreshing } ]">
      <slot></slot>
    </div>
  </div>
</template>
